/**
 * Client-side ranking for contextual recommendations (#43).
 * Complementary = different product classification than items already in context (cart / PDP).
 */

export function dedupeStoreItemsByProductId(items: unknown[]): any[] {
  const seen = new Set<string>();
  const out: any[] = [];
  for (const raw of items) {
    const item = raw as any;
    const id = item?.product?.id || item?.productId || item?.id;
    if (!id || typeof id !== "string" || seen.has(id)) continue;
    seen.add(id);
    out.push(item);
  }
  return out;
}

/** At least one purchasable variant / package (matches storefront rules). */
export function isStoreItemPurchasable(item: any): boolean {
  if (!item) return false;
  if (Array.isArray(item.sizeOptions) && item.sizeOptions.length > 0) {
    return item.sizeOptions.some((o: any) => Number(o?.inStock ?? 0) >= 1);
  }
  return Number(item.inStock ?? 0) >= 1;
}

export type RankRecommendationsOpts = {
  excludeProductIds: Set<string>;
  /** Classifications already represented in cart / on PDP, others rank higher (complementary). */
  primaryClassifications: Set<string>;
  limit?: number;
};

/**
 * Filter to purchasable, exclude ids, then sort: complementary first, then stock depth, then purchase signal.
 */
export function rankStoreRecommendations(
  items: unknown[],
  opts: RankRecommendationsOpts
): any[] {
  const limit = opts.limit ?? 24;
  const filtered = dedupeStoreItemsByProductId(items).filter((item) => {
    const id = item?.product?.id || item?.productId || item?.id;
    if (!id || opts.excludeProductIds.has(id)) return false;
    return isStoreItemPurchasable(item);
  });

  const stockDepth = (item: any): number => {
    if (Array.isArray(item.sizeOptions) && item.sizeOptions.length > 0) {
      return item.sizeOptions.reduce(
        (sum: number, o: any) => sum + Math.max(0, Number(o?.inStock ?? 0)),
        0
      );
    }
    return Math.max(0, Number(item.inStock ?? 0));
  };

  const scored = filtered.map((item) => {
    const cls = String(item?.product?.classificationName ?? "").trim();
    const norm = cls.toLowerCase();
    const primary = opts.primaryClassifications;
    const complementary =
      norm.length > 0 &&
      primary.size > 0 &&
      ![...primary].some((p) => p.trim().toLowerCase() === norm);
    const purchases = Number(item?.product?.purchaseCount ?? 0);
    const score =
      (complementary ? 1_000_000 : 0) +
      stockDepth(item) * 100 +
      purchases;
    return { item, score };
  });

  scored.sort((a, b) => b.score - a.score);
  return scored.slice(0, limit).map((s) => s.item);
}

export type PdpRecommendationCtx = {
  excludeProductIds: Set<string>;
  primaryClassification: string;
};

/** Merge same-category row with best-sellers pool; rank with complementary boost. */
export function mergePdpRecommendations(
  sameCategoryRows: any[],
  bestSellerRows: any[],
  ctx: PdpRecommendationCtx
): any[] {
  const primary = ctx.primaryClassification.trim()
    ? new Set([ctx.primaryClassification.trim()])
    : new Set<string>();
  const combined = [...sameCategoryRows, ...bestSellerRows];
  return rankStoreRecommendations(combined, {
    excludeProductIds: ctx.excludeProductIds,
    primaryClassifications: primary,
    limit: 4,
  });
}
